/**
 * Accounting & Developer — PIN-gated (565968) admin section.
 * Three sub-tabs: Driver Onboarding · Third Party Fees · Developer Notes.
 *
 * Authentication model:
 *   1. User opens the Accounting tab → empty PIN field shown.
 *   2. They enter PIN 565968 → POST /admin/accounting/unlock returns 200.
 *   3. On success we store the PIN in memory (NEVER localStorage) and pass it
 *      as a query/body param on every subsequent call. The backend re-checks
 *      the PIN every time.
 *
 * Note: deliberately PIN-gated to a SINGLE constant ("565968") instead of the
 * per-account pin_hash check used elsewhere, because the user spec calls this
 * "Derick-only" and Derick is the keeper of the master 565968 PIN.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import adminApi from "@/lib/adminApi";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Lock, Unlock, FileText, Download, ShieldCheck, AlertTriangle, Eye, CheckCircle2, Ban, Search, DollarSign, NotebookPen, Trash2, Plus, FileDown, RefreshCw, X as XIcon, Edit3, RotateCcw, Loader2 } from "lucide-react";
import { fmtUsd } from "@/lib/format";
import jsPDF from "jspdf";

export default function AccountingTab() {
  // v2.6.9 batch Q-2 · per-tab PIN prompt (replaces the route-level AdminGate
  // that was wrapping the whole admin console). Same UX as the TAXIcab
  // Commander tab — supervisor enters their per-account PIN once, it stays
  // unlocked for the rest of the session, and every server call attaches
  // it as `?pin=` so the existing _require_accounting_access checks pass.
  const [pin, setPin] = useState("");
  const [pinDraft, setPinDraft] = useState("");
  const [unlockBusy, setUnlockBusy] = useState(false);
  const unlock = async (e) => {
    e?.preventDefault();
    if (!pinDraft) return;
    setUnlockBusy(true);
    try {
      await adminApi.post("/admin/accounting/unlock", { pin: pinDraft });
      setPin(pinDraft);
      toast.success("Accounting unlocked");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "PIN does not match your account");
      setPinDraft("");
    } finally { setUnlockBusy(false); }
  };
  const [sub, setSub] = useState("onboarding");

  if (!pin) {
    return (
      <Card className="bg-zinc-900 border-amber-400/40 p-6 max-w-md mx-auto" data-testid="accounting-locked">
        <div className="flex items-center gap-3 mb-3">
          <Lock className="h-5 w-5 text-amber-400"/>
          <div>
            <h3 className="font-bold">Accounting & Developer</h3>
            <p className="text-[11px] text-zinc-500">Enter your supervisor / admin PIN to access onboarding decisions, third-party fee rollups, and developer notes.</p>
          </div>
        </div>
        <form onSubmit={unlock} className="flex gap-2">
          <Input data-testid="accounting-pin-input" type="password" inputMode="numeric" autoFocus value={pinDraft} onChange={(e) => setPinDraft(e.target.value)} placeholder="PIN" className="bg-zinc-950 border-zinc-700"/>
          <Button data-testid="accounting-unlock-btn" type="submit" disabled={unlockBusy} className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold">
            {unlockBusy ? <Loader2 className="h-4 w-4 animate-spin"/> : "Unlock"}
          </Button>
        </form>
      </Card>
    );
  }

  return (
    <div data-testid="accounting-tab-unlocked">
      <Card className="bg-zinc-900 border-amber-400/40 p-4 mb-4 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-amber-400"/>
          <h3 className="font-bold">Accounting &amp; Developer</h3>
          <Badge variant="outline" className="border-amber-400/40 text-amber-300 text-[10px] uppercase">PIN verified</Badge>
        </div>
      </Card>

      <Tabs value={sub} onValueChange={setSub}>
        <TabsList className="bg-zinc-900 border border-zinc-800 grid grid-cols-3 mb-4 h-auto" data-testid="accounting-subtabs">
          <TabsTrigger value="onboarding" data-testid="acc-tab-onboarding"><ShieldCheck className="h-4 w-4 mr-1"/>Driver Onboarding</TabsTrigger>
          <TabsTrigger value="fees" data-testid="acc-tab-fees"><DollarSign className="h-4 w-4 mr-1"/>Third Party Fees</TabsTrigger>
          <TabsTrigger value="notes" data-testid="acc-tab-notes"><NotebookPen className="h-4 w-4 mr-1"/>Developer Notes</TabsTrigger>
        </TabsList>
        <TabsContent value="onboarding"><OnboardingPanel pin={pin}/></TabsContent>
        <TabsContent value="fees"><FeesPanel pin={pin}/></TabsContent>
        <TabsContent value="notes"><NotesPanel pin={pin}/></TabsContent>
      </Tabs>
    </div>
  );
}

// =========================================================================
// Sub-tab 1: Driver Onboarding
// =========================================================================
const COMPLIANCE_LABELS = {
  approve: { label: "APPROVED", cls: "text-green-400 border-green-400/40 bg-green-400/10" },
  suspend: { label: "SUSPENDED", cls: "text-red-400 border-red-400/40 bg-red-400/10" },
  under_investigation: { label: "UNDER INVESTIGATION", cls: "text-orange-400 border-orange-400/40 bg-orange-400/10" },
  clear: { label: "CLEARED", cls: "text-zinc-300 border-zinc-600 bg-zinc-800" },
};

function OnboardingPanel({ pin }) {
  const [drivers, setDrivers] = useState([]);
  const [search, setSearch] = useState("");
  const [openDriver, setOpenDriver] = useState(null);
  const [decision, setDecision] = useState({ action: "approve", reason: "" });
  const [busy, setBusy] = useState(false);

  const reload = () => adminApi.get("/admin/accounting/onboarding/drivers", { params: { pin } })
    .then(r => setDrivers(r.data.drivers || []))
    .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load drivers"));

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return drivers;
    return drivers.filter(d => (
      (d.name || "").toLowerCase().includes(q)
      || (d.email || "").toLowerCase().includes(q)
      || (d.phone || "").includes(q)
      || (d.plate_number || "").toLowerCase().includes(q)
    ));
  }, [drivers, search]);

  const submitDecision = async () => {
    if (!openDriver) return;
    setBusy(true);
    try {
      await adminApi.post(`/admin/accounting/onboarding/drivers/${openDriver.id}/decision`, {
        pin, action: decision.action, reason: decision.reason,
      });
      toast.success(`${openDriver.name || openDriver.email} · ${decision.action.toUpperCase()}`);
      setOpenDriver(null);
      setDecision({ action: "approve", reason: "" });
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Decision failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="onboarding-panel">
      <Card className="bg-zinc-900 border-zinc-800 p-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h3 className="font-bold flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-amber-400"/>Driver Onboarding</h3>
            <p className="text-[11px] text-zinc-500 mt-0.5">Approve, suspend, or place under investigation. Decision applies immediately and is logged to the audit trail.</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="h-3 w-3 absolute left-2 top-2.5 text-zinc-500"/>
              <Input data-testid="onboarding-search" value={search} onChange={(e)=>setSearch(e.target.value)} placeholder="Search name/email/plate…" className="bg-zinc-950 border-zinc-700 text-xs h-8 pl-7 w-60"/>
            </div>
            <Button variant="outline" size="sm" onClick={reload} className="bg-zinc-950 border-zinc-700 h-8 text-xs" data-testid="onboarding-refresh"><RefreshCw className="h-3 w-3 mr-1"/>Refresh</Button>
          </div>
        </div>
      </Card>

      {filtered.length === 0 ? (
        <Card className="bg-zinc-900 border-zinc-800 p-10 text-center text-zinc-500 text-sm">No drivers match.</Card>
      ) : (
        <div className="space-y-2">
          {filtered.map(d => {
            const cs = COMPLIANCE_LABELS[d.compliance_status];
            return (
              <Card key={d.id} className="bg-zinc-900 border-zinc-800 p-4" data-testid={`onboarding-row-${d.id}`}>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <div className="md:col-span-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold">{d.name || "—"}</span>
                      <Badge variant="outline" className="text-[10px] uppercase border-zinc-700">{d.role}</Badge>
                      {d.verified && <Badge variant="outline" className="text-[10px] uppercase border-green-400/40 text-green-400">verified</Badge>}
                      {d.active === false && <Badge variant="outline" className="text-[10px] uppercase border-red-400/40 text-red-400">inactive</Badge>}
                      {cs && <Badge variant="outline" className={`text-[10px] uppercase ${cs.cls}`}>{cs.label}</Badge>}
                    </div>
                    <div className="text-[11px] text-zinc-400 mt-1">{d.email} · {d.phone || "no phone"} · {d.license_number || "no license"}</div>
                    {d.compliance_reason && <div className="text-[10px] text-amber-400 mt-0.5">⚠ {d.compliance_reason}</div>}
                  </div>
                  <div className="text-[11px] text-zinc-400 md:col-span-1">
                    {d.vehicle_make || d.vehicle_model ? (
                      <>
                        <div>{[d.vehicle_year, d.vehicle_color, d.vehicle_make, d.vehicle_model].filter(Boolean).join(" ")}</div>
                        <div>{d.plate_number || "no plate"}</div>
                      </>
                    ) : <div className="text-zinc-600">No vehicle on file</div>}
                  </div>
                  <div className="flex items-start gap-2 justify-end">
                    <Button size="sm" variant="outline" onClick={() => setOpenDriver(d)} className="bg-zinc-950 border-amber-400/40 text-amber-300 h-8 text-xs" data-testid={`onboarding-review-${d.id}`}>
                      <Eye className="h-3 w-3 mr-1"/>Review
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Review / Decision Dialog */}
      <Dialog open={!!openDriver} onOpenChange={(o) => { if (!o) setOpenDriver(null); }}>
        <DialogContent className="bg-zinc-900 border-amber-400/40 text-zinc-100 max-w-2xl" data-testid="onboarding-decision-dialog">
          <DialogHeader><DialogTitle className="text-amber-400">Driver Review · {openDriver?.name || openDriver?.email}</DialogTitle></DialogHeader>
          {openDriver && (
            <div className="space-y-3 text-xs">
              <DriverProfileGrid d={openDriver}/>
              <div className="border-t border-zinc-800 pt-3 space-y-2">
                <Label className="text-xs uppercase tracking-wider text-zinc-400">Decision</Label>
                <Select value={decision.action} onValueChange={(v) => setDecision({ ...decision, action: v })}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-700 text-zinc-100" data-testid="onboarding-action"><SelectValue/></SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-700 text-zinc-100">
                    <SelectItem value="approve">Approve · activate &amp; verify</SelectItem>
                    <SelectItem value="suspend">Suspend · deactivate immediately</SelectItem>
                    <SelectItem value="under_investigation">Under investigation · deactivate &amp; flag</SelectItem>
                    <SelectItem value="clear">Clear · remove compliance flag</SelectItem>
                  </SelectContent>
                </Select>
                <Label className="text-xs uppercase tracking-wider text-zinc-400">Reason / notes (optional)</Label>
                <Input data-testid="onboarding-reason" value={decision.reason} onChange={(e)=>setDecision({...decision, reason: e.target.value})} placeholder="Reason logged to audit trail" className="bg-zinc-950 border-zinc-700 text-zinc-100"/>
              </div>
            </div>
          )}
          <DialogFooter className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setOpenDriver(null)} className="bg-zinc-950 border-zinc-700 text-zinc-300">Cancel</Button>
            <Button data-testid="onboarding-decision-submit" onClick={submitDecision} disabled={busy} className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold">
              {busy ? "Saving…" : "Confirm decision"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DriverProfileGrid({ d }) {
  const rows = [
    ["Email", d.email],
    ["Phone", d.phone || "—"],
    ["Address", d.address || "—"],
    ["License #", d.license_number || "—"],
    ["Tax / SSN", d.tax_number || "—"],
    ["DOB", d.date_of_birth || "—"],
    ["Vehicle", [d.vehicle_year, d.vehicle_color, d.vehicle_make, d.vehicle_model].filter(Boolean).join(" ") || "—"],
    ["Plate", d.plate_number || "—"],
    ["Insurance doc", d.insurance_doc_url ? "Uploaded" : "—"],
    ["Registration doc", d.registration_doc_url ? "Uploaded" : "—"],
    ["Account created", d.created_at || "—"],
    ["Last compliance action", `${d.compliance_status || "—"} on ${d.compliance_updated_at || "—"} by ${d.compliance_updated_by || "—"}`],
  ];
  return (
    <div className="grid grid-cols-2 gap-x-3 gap-y-1">
      {rows.map(([k, v]) => (
        <div key={k} className="flex justify-between gap-2 border-b border-zinc-800 py-1">
          <span className="text-zinc-500 uppercase tracking-wider text-[10px]">{k}</span>
          <span className="text-zinc-200 truncate max-w-[60%] text-right">{v}</span>
        </div>
      ))}
    </div>
  );
}

// =========================================================================
// Sub-tab 2: Third Party Fees
// =========================================================================
const FEE_CATEGORIES = [
  { key: "maintenance", label: "Technology Fee ($1.10/pax)", color: "text-amber-300" },
  { key: "franchise", label: "Franchise ($1.00/ride)", color: "text-blue-300" },
  { key: "tax", label: "Taxes (9.5%)", color: "text-green-300" },
];

function FeesPanel({ pin }) {
  const [rollup, setRollup] = useState([]);
  const [totals, setTotals] = useState(null);
  const [days, setDays] = useState(10);
  const [busy, setBusy] = useState(false);
  const [openPaid, setOpenPaid] = useState(null);   // { date, category, computed_amount }
  const [paidForm, setPaidForm] = useState({ method: "venmo", reference: "", amount: "" });
  const [openOverride, setOpenOverride] = useState(null);  // full row obj
  const [overrideForm, setOverrideForm] = useState({ maintenance_total: "", franchise_total: "", tax_total: "", ride_count: "", reason: "" });
  // v2.6.9 batch X · per-day drilldown
  const [detailDate, setDetailDate] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [detailBusy, setDetailBusy] = useState(false);

  useEffect(() => {
    if (!detailDate) { setDetailData(null); return; }
    setDetailBusy(true);
    adminApi.get(`/admin/accounting/third-party-fees/${detailDate}/detail`, { params: { pin } })
      .then(r => setDetailData(r.data))
      .catch(e => toast.error(e?.response?.data?.detail || "Failed to load drilldown"))
      .finally(() => setDetailBusy(false));
  }, [detailDate, pin]);

  const reload = () => {
    setBusy(true);
    adminApi.get("/admin/accounting/third-party-fees", { params: { pin, days } })
      .then(r => { setRollup(r.data.rollup || []); setTotals(r.data.totals || null); })
      .catch(e => toast.error(e?.response?.data?.detail || "Failed to load fees"))
      .finally(() => setBusy(false));
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [days]);

  const markPaid = async () => {
    if (!openPaid) return;
    setBusy(true);
    try {
      await adminApi.post(`/admin/accounting/third-party-fees/${openPaid.date}/mark-paid`, {
        pin, category: openPaid.category, method: paidForm.method,
        reference: paidForm.reference, amount: parseFloat(paidForm.amount) || openPaid.computed_amount || 0,
      });
      toast.success(`Marked ${openPaid.category} as paid for ${openPaid.date}`);
      setOpenPaid(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Mark-paid failed");
    } finally { setBusy(false); }
  };

  const unmarkPaid = async (date, category) => {
    if (!window.confirm(`Remove paid marker for ${category} on ${date}?`)) return;
    try {
      await adminApi.delete(`/admin/accounting/third-party-fees/${date}/${category}/mark-paid`, { params: { pin } });
      toast.message("Paid marker removed");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const openOverrideDialog = (row) => {
    setOpenOverride(row);
    setOverrideForm({
      maintenance_total: row.maintenance_total.toFixed(2),
      franchise_total: row.franchise_total.toFixed(2),
      tax_total: row.tax_total.toFixed(2),
      ride_count: String(row.ride_count),
      reason: "",
    });
  };

  const applyOverride = async (zeroOut = false) => {
    if (!openOverride) return;
    if (zeroOut && !window.confirm(`Clear ALL fee data for ${openOverride.date}? This excludes the day from your rollup. (Reversible via "Restore computed".)`)) return;
    setBusy(true);
    try {
      const payload = {
        pin,
        reason: overrideForm.reason || (zeroOut ? "Zeroed out — bad data" : "Manual override"),
        ...(zeroOut
          ? { maintenance_total: 0, franchise_total: 0, tax_total: 0, ride_count: 0 }
          : {
              maintenance_total: parseFloat(overrideForm.maintenance_total),
              franchise_total: parseFloat(overrideForm.franchise_total),
              tax_total: parseFloat(overrideForm.tax_total),
              ride_count: parseInt(overrideForm.ride_count, 10) || 0,
            }),
      };
      await adminApi.post(`/admin/accounting/third-party-fees/${openOverride.date}/override`, payload);
      toast.success(zeroOut ? `${openOverride.date} cleared from rollup` : `${openOverride.date} overridden`);
      setOpenOverride(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Override failed");
    } finally { setBusy(false); }
  };

  const restoreComputed = async (date) => {
    if (!window.confirm(`Restore computed totals for ${date}? This removes the manual override.`)) return;
    try {
      await adminApi.delete(`/admin/accounting/third-party-fees/${date}/override`, { params: { pin } });
      toast.message(`Override removed for ${date} — using computed values`);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  // Per-day CSV export (uses the same export endpoint with ?date= filter)
  const exportRowCsv = (date) => {
    const url = `${adminApi.defaults.baseURL}/admin/accounting/third-party-fees/export.csv?pin=${encodeURIComponent(pin)}&days=${days}&date=${encodeURIComponent(date)}`;
    fetch(url, { headers: adminApi.defaults.headers.common })
      .then(r => r.blob())
      .then(blob => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `buller-fees-${date}.csv`;
        link.click();
      })
      .catch(() => toast.error("Day CSV export failed"));
  };

  // Per-day PDF export (built locally from the row in state, no extra round-trip)
  const exportRowPdf = (r) => {
    const doc = new jsPDF({ unit: "pt", format: "letter" });
    doc.setFontSize(15); doc.text("Buller Taxi · Third Party Fees", 40, 50);
    doc.setFontSize(11); doc.text(`Date: ${r.date}`, 40, 72);
    doc.setFontSize(9); doc.text(`Exported ${new Date().toISOString()}`, 40, 88);
    let y = 120;
    doc.setFontSize(10); doc.setFont(undefined, "bold");
    doc.text("Category", 40, y); doc.text("Amount", 240, y); doc.text("Paid", 340, y); doc.text("Method", 400, y); doc.text("Reference", 470, y);
    doc.setFont(undefined, "normal"); y += 14;
    const rows = [
      ["Technology Fee ($1.10/pax)", r.maintenance_total, r.paid?.maintenance],
      ["Franchise ($1.00/ride)", r.franchise_total, r.paid?.franchise],
      ["Taxes (9.5%)", r.tax_total, r.paid?.tax],
    ];
    rows.forEach(([label, amt, paid]) => {
      doc.text(label, 40, y);
      doc.text(`$${amt.toFixed(2)}`, 240, y);
      doc.text(paid ? "YES" : "—", 340, y);
      doc.text(paid?.method || "—", 400, y);
      doc.text(paid?.reference || "—", 470, y);
      y += 14;
    });
    y += 8;
    doc.setFont(undefined, "bold"); doc.text("Grand total", 40, y); doc.text(`$${r.grand_total.toFixed(2)}`, 240, y); doc.setFont(undefined, "normal");
    y += 24; doc.setFontSize(9);
    doc.text(`Rides counted: ${r.ride_count}`, 40, y);
    if (r.override) {
      y += 14;
      doc.setFont(undefined, "italic");
      doc.text(`Manual override applied ${r.override.applied_at || ""} by ${r.override.applied_by || ""} — ${r.override.reason || ""}`, 40, y, { maxWidth: 520 });
    }
    doc.save(`buller-fees-${r.date}.pdf`);
  };

  // Bulk export buttons (header)
  const exportCsv = () => {
    const url = `${adminApi.defaults.baseURL}/admin/accounting/third-party-fees/export.csv?pin=${encodeURIComponent(pin)}&days=${days}`;
    fetch(url, { headers: adminApi.defaults.headers.common })
      .then(r => r.blob())
      .then(blob => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `buller-third-party-fees-${days}d.csv`;
        link.click();
      })
      .catch(() => toast.error("CSV export failed"));
  };

  const exportPdf = () => {
    const doc = new jsPDF({ unit: "pt", format: "letter" });
    doc.setFontSize(14); doc.text("Buller Taxi · Third Party Fees", 40, 40);
    doc.setFontSize(10); doc.text(`Last ${days} days · Exported ${new Date().toISOString()}`, 40, 58);
    let y = 90;
    doc.setFontSize(8);
    doc.text("DATE", 40, y); doc.text("RIDES", 130, y); doc.text("MAINT", 180, y); doc.text("FRANCH", 240, y); doc.text("TAX", 300, y); doc.text("TOTAL", 360, y); doc.text("PAID", 420, y);
    y += 12;
    rollup.forEach(r => {
      if (y > 730) { doc.addPage(); y = 40; }
      doc.text(r.date, 40, y);
      doc.text(String(r.ride_count), 130, y);
      doc.text(`$${r.maintenance_total.toFixed(2)}`, 180, y);
      doc.text(`$${r.franchise_total.toFixed(2)}`, 240, y);
      doc.text(`$${r.tax_total.toFixed(2)}`, 300, y);
      doc.text(`$${r.grand_total.toFixed(2)}`, 360, y);
      const paid = r.paid || {};
      const flags = ["maintenance","franchise","tax"].map(c => paid[c] ? c[0].toUpperCase() : "·").join("");
      doc.text(flags, 420, y);
      y += 12;
    });
    doc.save(`buller-third-party-fees-${days}d.pdf`);
  };

  return (
    <div className="space-y-3" data-testid="fees-panel">
      <Card className="bg-zinc-900 border-zinc-800 p-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h3 className="font-bold flex items-center gap-2"><DollarSign className="h-5 w-5 text-amber-400"/>Third Party Fees · nightly rollup</h3>
            <p className="text-[11px] text-zinc-500 mt-0.5">Calendar-day buckets (UTC midnight). Calculated from every completed ride. Click "Adjust" on a row to fix bad data or clear the day; each row also has its own CSV/PDF export.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Label className="text-xs text-zinc-400">Days</Label>
            <Input data-testid="fees-days" type="number" min={1} max={365} value={days} onChange={(e)=>setDays(Math.max(1, Math.min(365, parseInt(e.target.value||"10",10))))} className="bg-zinc-950 border-zinc-700 text-zinc-100 h-8 w-20 text-xs"/>
            <Button variant="outline" size="sm" onClick={reload} className="bg-zinc-950 border-zinc-700 h-8 text-xs" data-testid="fees-refresh"><RefreshCw className="h-3 w-3 mr-1"/>Refresh</Button>
            <Button variant="outline" size="sm" onClick={exportCsv} className="bg-zinc-950 border-green-400/40 text-green-300 h-8 text-xs" data-testid="fees-export-csv"><FileDown className="h-3 w-3 mr-1"/>CSV all</Button>
            <Button variant="outline" size="sm" onClick={exportPdf} className="bg-zinc-950 border-blue-400/40 text-blue-300 h-8 text-xs" data-testid="fees-export-pdf"><Download className="h-3 w-3 mr-1"/>PDF all</Button>
          </div>
        </div>
        {totals && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3 text-xs">
            <Pill label="Rides" value={totals.ride_count}/>
            <Pill label="Technology Fee" value={fmtUsd(totals.maintenance_total)} color="text-amber-300"/>
            <Pill label="Franchise" value={fmtUsd(totals.franchise_total)} color="text-blue-300"/>
            <Pill label="Taxes" value={fmtUsd(totals.tax_total)} color="text-green-300"/>
          </div>
        )}
      </Card>

      <Card className="bg-zinc-900 border-zinc-800 overflow-hidden overflow-x-auto">
        <table className="w-full text-xs min-w-[860px]" data-testid="fees-table">
          <thead>
            <tr className="border-b border-zinc-800 text-[10px] uppercase tracking-wider text-zinc-500">
              <th className="text-left p-3">Date</th>
              <th className="text-right p-3">Rides</th>
              <th className="text-right p-3">Technology Fee</th>
              <th className="text-right p-3">Franchise</th>
              <th className="text-right p-3">Taxes</th>
              <th className="text-right p-3">Grand Total</th>
              <th className="text-center p-3">Status</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rollup.length === 0 && (
              <tr><td colSpan={8} className="p-10 text-center text-zinc-500">No data for the selected window.</td></tr>
            )}
            {rollup.map(r => (
              <tr
                key={r.date}
                className={`border-b border-zinc-900 hover:bg-zinc-900/60 ${r.override ? "bg-orange-500/5" : ""} cursor-pointer`}
                data-testid={`fees-row-${r.date}`}
                onClick={(e) => {
                  // Don't open detail if user clicked a button/badge inside the row
                  const target = e.target;
                  if (target.closest && target.closest("button, .badge, [data-no-row-open]")) return;
                  setDetailDate(r.date);
                }}
                title="Click row for per-ride drilldown"
              >
                <td className="p-3 font-mono">
                  {r.date}
                  {r.override && (
                    <Badge variant="outline" className="ml-2 border-orange-400/40 text-orange-300 text-[9px] uppercase" title={`Override by ${r.override.applied_by} · ${r.override.reason || ""}`}>OVR</Badge>
                  )}
                </td>
                <td className="p-3 text-right">{r.ride_count}</td>
                {FEE_CATEGORIES.map(c => {
                  const total = r[`${c.key}_total`];
                  const paid = r.paid?.[c.key];
                  return (
                    <td key={c.key} className={`p-3 text-right ${c.color}`}>
                      <div className="flex items-center justify-end gap-2">
                        <span>{fmtUsd(total)}</span>
                        {paid ? (
                          <Badge data-testid={`fee-paid-${r.date}-${c.key}`} variant="outline" className="border-green-400/40 text-green-400 text-[9px] uppercase cursor-pointer hover:bg-red-500/10 hover:text-red-400 hover:border-red-400/40" onClick={()=>unmarkPaid(r.date, c.key)} title="Click to remove">
                            <CheckCircle2 className="h-3 w-3 mr-0.5"/>{paid.method}
                          </Badge>
                        ) : (
                          <Button data-testid={`fee-pay-${r.date}-${c.key}`} size="sm" variant="ghost" disabled={total <= 0} onClick={()=>{ setOpenPaid({ date: r.date, category: c.key, computed_amount: total }); setPaidForm({ method: "venmo", reference: "", amount: total.toFixed(2) }); }} className="h-6 px-2 text-[10px] text-zinc-400 hover:text-amber-300">Mark paid</Button>
                        )}
                      </div>
                    </td>
                  );
                })}
                <td className="p-3 text-right font-bold">{fmtUsd(r.grand_total)}</td>
                <td className="p-3 text-center">
                  {Object.keys(r.paid || {}).length === 3 ? (
                    <Badge variant="outline" className="border-green-400/40 text-green-400 text-[10px] uppercase">All paid</Badge>
                  ) : Object.keys(r.paid || {}).length > 0 ? (
                    <Badge variant="outline" className="border-amber-400/40 text-amber-400 text-[10px] uppercase">Partial</Badge>
                  ) : (
                    <Badge variant="outline" className="border-zinc-600 text-zinc-400 text-[10px] uppercase">Open</Badge>
                  )}
                </td>
                <td className="p-3">
                  <div className="flex justify-end items-center gap-1">
                    <Button data-testid={`fees-csv-${r.date}`} size="icon" variant="ghost" title={`Export CSV for ${r.date}`} onClick={()=>exportRowCsv(r.date)} className="h-7 w-7 text-zinc-400 hover:text-green-300">
                      <FileDown className="h-3.5 w-3.5"/>
                    </Button>
                    <Button data-testid={`fees-pdf-${r.date}`} size="icon" variant="ghost" title={`Export PDF for ${r.date}`} onClick={()=>exportRowPdf(r)} className="h-7 w-7 text-zinc-400 hover:text-blue-300">
                      <Download className="h-3.5 w-3.5"/>
                    </Button>
                    <Button data-testid={`fees-adjust-${r.date}`} size="icon" variant="ghost" title={`Adjust / fix bad data for ${r.date}`} onClick={()=>openOverrideDialog(r)} className="h-7 w-7 text-zinc-400 hover:text-orange-300">
                      <Edit3 className="h-3.5 w-3.5"/>
                    </Button>
                    {r.override && (
                      <Button data-testid={`fees-restore-${r.date}`} size="icon" variant="ghost" title="Restore computed values" onClick={()=>restoreComputed(r.date)} className="h-7 w-7 text-zinc-400 hover:text-red-300">
                        <RotateCcw className="h-3.5 w-3.5"/>
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* v2.6.9 batch X · Per-day Third-Party Fees drilldown */}
      <Dialog open={!!detailDate} onOpenChange={(o) => { if (!o) setDetailDate(null); }}>
        <DialogContent className="bg-zinc-900 border-zinc-700 text-zinc-100 max-w-5xl max-h-[88vh] overflow-y-auto" data-testid="fees-detail-dialog">
          <DialogHeader>
            <DialogTitle className="text-amber-400">Third Party Fees · {detailDate} · per-ride detail</DialogTitle>
          </DialogHeader>
          {detailBusy && <div className="py-8 text-center text-zinc-500 text-sm flex items-center justify-center gap-2"><Loader2 className="h-4 w-4 animate-spin"/>Loading drilldown…</div>}
          {!detailBusy && detailData && (
            <div>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3">
                <Card className="bg-zinc-950 border-zinc-800 p-2">
                  <div className="text-[9px] uppercase text-zinc-500">Rides</div>
                  <div className="font-bold text-base">{detailData.totals.ride_count}</div>
                </Card>
                <Card className="bg-zinc-950 border-zinc-800 p-2">
                  <div className="text-[9px] uppercase text-zinc-500">Tech Fee</div>
                  <div className="font-bold text-base text-amber-300">{fmtUsd(detailData.totals.tech_total)}</div>
                </Card>
                <Card className="bg-zinc-950 border-zinc-800 p-2">
                  <div className="text-[9px] uppercase text-zinc-500">Franchise</div>
                  <div className="font-bold text-base text-yellow-300">{fmtUsd(detailData.totals.franchise_total)}</div>
                </Card>
                <Card className="bg-zinc-950 border-zinc-800 p-2">
                  <div className="text-[9px] uppercase text-zinc-500">Sales Tax</div>
                  <div className="font-bold text-base text-purple-300">{fmtUsd(detailData.totals.tax_total)}</div>
                </Card>
                <Card className="bg-zinc-950 border-amber-400/40 p-2">
                  <div className="text-[9px] uppercase text-amber-300">Grand Total</div>
                  <div className="font-extrabold text-base text-amber-400">{fmtUsd(detailData.totals.grand_total)}</div>
                </Card>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="text-[10px] uppercase text-zinc-500 border-b border-zinc-800">
                    <tr>
                      <th className="text-left p-2">When</th>
                      <th className="text-left p-2">Customer</th>
                      <th className="text-left p-2">Driver</th>
                      <th className="text-left p-2">Route</th>
                      <th className="text-right p-2">Pax</th>
                      <th className="text-right p-2">Ride $</th>
                      <th className="text-right p-2">Tech</th>
                      <th className="text-right p-2">Franc.</th>
                      <th className="text-right p-2">Tax</th>
                      <th className="text-right p-2">3P Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailData.rides.length === 0 && (
                      <tr><td colSpan={10} className="p-10 text-center text-zinc-500 italic">No rides on this date</td></tr>
                    )}
                    {detailData.rides.map((rd) => (
                      <tr key={rd.ride_id} className="border-b border-zinc-900 hover:bg-zinc-900/60" data-testid={`fees-detail-row-${rd.ride_id}`}>
                        <td className="p-2 text-zinc-400 whitespace-nowrap">{rd.completed_at ? new Date(rd.completed_at).toLocaleTimeString() : "—"}</td>
                        <td className="p-2">{rd.customer_name}</td>
                        <td className="p-2">{rd.driver_name}</td>
                        <td className="p-2 max-w-[220px] truncate text-zinc-400" title={`${rd.pickup} → ${rd.dropoff}`}>{rd.pickup} → {rd.dropoff}</td>
                        <td className="p-2 text-right">{rd.passengers}</td>
                        <td className="p-2 text-right meter-display">{fmtUsd(rd.ride_total)}</td>
                        <td className="p-2 text-right meter-display text-amber-300">{fmtUsd(rd.tech_fee)}</td>
                        <td className="p-2 text-right meter-display text-yellow-300">{fmtUsd(rd.franchise_fee)}</td>
                        <td className="p-2 text-right meter-display text-purple-300">{fmtUsd(rd.sales_tax)}</td>
                        <td className="p-2 text-right meter-display font-bold text-amber-400">{fmtUsd(rd.third_party_total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <DialogFooter className="border-t border-zinc-800 pt-3">
            <Button variant="ghost" onClick={() => setDetailDate(null)}><XIcon className="h-4 w-4 mr-1"/>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark-paid dialog */}
      <Dialog open={!!openPaid} onOpenChange={(o) => { if (!o) setOpenPaid(null); }}>
        <DialogContent className="bg-zinc-900 border-amber-400/40 text-zinc-100" data-testid="fee-pay-dialog">
          <DialogHeader><DialogTitle className="text-amber-400">Mark fee paid · {openPaid?.category} · {openPaid?.date}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-400">Method</Label>
              <Select value={paidForm.method} onValueChange={(v)=>setPaidForm({...paidForm, method: v})}>
                <SelectTrigger className="bg-zinc-950 border-zinc-700 text-zinc-100" data-testid="fee-pay-method"><SelectValue/></SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-700 text-zinc-100">
                  <SelectItem value="venmo">Venmo</SelectItem>
                  <SelectItem value="debit">Debit card</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="ach">ACH / Bank transfer</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-400">Amount ($)</Label>
              <Input data-testid="fee-pay-amount" type="number" step="0.01" value={paidForm.amount} onChange={(e)=>setPaidForm({...paidForm, amount: e.target.value})} className="bg-zinc-950 border-zinc-700 text-zinc-100"/>
              <div className="text-[10px] text-zinc-500 mt-1">Computed amount for this row: {fmtUsd(openPaid?.computed_amount)}</div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-400">Reference / transaction id</Label>
              <Input data-testid="fee-pay-ref" value={paidForm.reference} onChange={(e)=>setPaidForm({...paidForm, reference: e.target.value})} placeholder="e.g. Venmo tx id, check #, bank trace" className="bg-zinc-950 border-zinc-700 text-zinc-100"/>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpenPaid(null)} className="bg-zinc-950 border-zinc-700">Cancel</Button>
            <Button data-testid="fee-pay-submit" onClick={markPaid} disabled={busy} className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold">Confirm paid</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Override / fix-bad-data dialog */}
      <Dialog open={!!openOverride} onOpenChange={(o) => { if (!o) setOpenOverride(null); }}>
        <DialogContent className="bg-zinc-900 border-orange-400/40 text-zinc-100 max-w-lg" data-testid="fee-override-dialog">
          <DialogHeader>
            <DialogTitle className="text-orange-400">Adjust day · {openOverride?.date}</DialogTitle>
          </DialogHeader>
          <p className="text-[11px] text-zinc-400 -mt-1">Edit the totals manually if the auto-computed values are wrong (e.g. test rides, bad data), or click <b className="text-red-300">Clear day</b> to zero everything out. You can restore the computed values any time.</p>
          <div className="grid grid-cols-2 gap-3 mt-2">
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-400">Technology Fee ($)</Label>
              <Input data-testid="ov-maint" type="number" step="0.01" value={overrideForm.maintenance_total} onChange={(e)=>setOverrideForm({...overrideForm, maintenance_total: e.target.value})} className="bg-zinc-950 border-zinc-700 text-zinc-100"/>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-400">Franchise ($)</Label>
              <Input data-testid="ov-franchise" type="number" step="0.01" value={overrideForm.franchise_total} onChange={(e)=>setOverrideForm({...overrideForm, franchise_total: e.target.value})} className="bg-zinc-950 border-zinc-700 text-zinc-100"/>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-400">Taxes ($)</Label>
              <Input data-testid="ov-tax" type="number" step="0.01" value={overrideForm.tax_total} onChange={(e)=>setOverrideForm({...overrideForm, tax_total: e.target.value})} className="bg-zinc-950 border-zinc-700 text-zinc-100"/>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-400">Rides count</Label>
              <Input data-testid="ov-ride-count" type="number" value={overrideForm.ride_count} onChange={(e)=>setOverrideForm({...overrideForm, ride_count: e.target.value})} className="bg-zinc-950 border-zinc-700 text-zinc-100"/>
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider text-zinc-400">Reason (for audit log)</Label>
              <Input data-testid="ov-reason" value={overrideForm.reason} onChange={(e)=>setOverrideForm({...overrideForm, reason: e.target.value})} placeholder="e.g. excluded 3 test rides" className="bg-zinc-950 border-zinc-700 text-zinc-100"/>
            </div>
          </div>
          <DialogFooter className="gap-2 flex-wrap">
            <Button data-testid="ov-clear-day" variant="outline" onClick={()=>applyOverride(true)} disabled={busy} className="bg-zinc-950 border-red-400/40 text-red-300 hover:bg-red-500/10 mr-auto">
              <Trash2 className="h-3 w-3 mr-1"/>Clear day
            </Button>
            <Button variant="outline" onClick={()=>setOpenOverride(null)} className="bg-zinc-950 border-zinc-700">Cancel</Button>
            <Button data-testid="ov-submit" onClick={()=>applyOverride(false)} disabled={busy} className="bg-orange-400 hover:bg-orange-300 text-zinc-950 font-bold">Save override</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Pill({ label, value, color }) {
  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded p-3">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className={`text-lg font-bold ${color || "text-zinc-100"}`}>{value}</div>
    </div>
  );
}

// =========================================================================
// Sub-tab 3: Developer Notes
// =========================================================================
function NotesPanel({ pin }) {
  const [notes, setNotes] = useState([]);
  const [draft, setDraft] = useState({ title: "", body: "" });
  const [busy, setBusy] = useState(false);

  const reload = () => adminApi.get("/admin/accounting/dev-notes", { params: { pin } })
    .then(r => setNotes(r.data.notes || []))
    .catch(e => toast.error(e?.response?.data?.detail || "Failed to load notes"));
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  const save = async () => {
    if (!draft.body.trim()) { toast.error("Note body required"); return; }
    setBusy(true);
    try {
      await adminApi.post("/admin/accounting/dev-notes", { pin, title: draft.title, body: draft.body });
      toast.success("Note saved");
      setDraft({ title: "", body: "" });
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const remove = async (noteId) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      await adminApi.delete(`/admin/accounting/dev-notes/${noteId}`, { params: { pin } });
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const exportWord = () => {
    const url = `${adminApi.defaults.baseURL}/admin/accounting/dev-notes/export.doc?pin=${encodeURIComponent(pin)}`;
    fetch(url, { headers: adminApi.defaults.headers.common })
      .then(r => r.blob())
      .then(blob => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "buller-developer-notes.doc";
        link.click();
      })
      .catch(() => toast.error("Word export failed"));
  };

  const exportPdf = () => {
    const doc = new jsPDF({ unit: "pt", format: "letter" });
    doc.setFontSize(14); doc.text("Buller Taxi · Developer Notes", 40, 40);
    doc.setFontSize(10); doc.text(`Exported ${new Date().toISOString()} · ${notes.length} note(s)`, 40, 58);
    let y = 90;
    notes.forEach(n => {
      if (y > 700) { doc.addPage(); y = 40; }
      doc.setFontSize(11); doc.setFont(undefined, "bold"); doc.text(n.title || "Untitled", 40, y); y += 14;
      doc.setFontSize(8); doc.setFont(undefined, "normal");
      doc.text(`${n.created_at || ""} · by ${n.created_by || ""}`, 40, y); y += 12;
      const lines = doc.splitTextToSize(n.body || "", 520);
      lines.forEach(line => {
        if (y > 740) { doc.addPage(); y = 40; }
        doc.text(line, 40, y); y += 11;
      });
      y += 8;
    });
    doc.save("buller-developer-notes.pdf");
  };

  return (
    <div className="space-y-3" data-testid="notes-panel">
      <Card className="bg-zinc-900 border-zinc-800 p-4">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div>
            <h3 className="font-bold flex items-center gap-2"><NotebookPen className="h-5 w-5 text-amber-400"/>Developer Notes</h3>
            <p className="text-[11px] text-zinc-500 mt-0.5">Personal log for the system owner. Notes are private and never shown to drivers/customers.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={exportWord} className="bg-zinc-950 border-blue-400/40 text-blue-300 h-8 text-xs" data-testid="notes-export-word"><FileDown className="h-3 w-3 mr-1"/>Word</Button>
            <Button variant="outline" size="sm" onClick={exportPdf} className="bg-zinc-950 border-red-400/40 text-red-300 h-8 text-xs" data-testid="notes-export-pdf"><Download className="h-3 w-3 mr-1"/>PDF</Button>
          </div>
        </div>
        <div className="space-y-2">
          <Input data-testid="note-title" value={draft.title} onChange={(e)=>setDraft({...draft, title: e.target.value})} placeholder="Title (optional)" className="bg-zinc-950 border-zinc-700 text-zinc-100"/>
          <textarea
            data-testid="note-body"
            value={draft.body}
            onChange={(e)=>setDraft({...draft, body: e.target.value})}
            rows={6}
            placeholder="Write your note here…"
            className="w-full bg-zinc-950 border border-zinc-700 text-zinc-100 rounded-md px-3 py-2 text-sm resize-y"
          />
          <div className="flex justify-end">
            <Button data-testid="note-save" onClick={save} disabled={busy || !draft.body.trim()} className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold">
              <Plus className="h-4 w-4 mr-1"/>Save note
            </Button>
          </div>
        </div>
      </Card>

      <div className="space-y-2">
        {notes.length === 0 ? (
          <Card className="bg-zinc-900 border-zinc-800 p-10 text-center text-zinc-500 text-sm">No notes yet — your saved notes will appear here.</Card>
        ) : notes.map(n => (
          <Card key={n.id} className="bg-zinc-900 border-zinc-800 p-4" data-testid={`note-row-${n.id}`}>
            <div className="flex items-start justify-between gap-2 mb-1">
              <div>
                <div className="font-bold text-sm">{n.title || "Untitled"}</div>
                <div className="text-[10px] text-zinc-500">{n.created_at} · by {n.created_by}</div>
              </div>
              <Button size="icon" variant="ghost" onClick={() => remove(n.id)} className="text-zinc-500 hover:text-red-400 h-8 w-8" data-testid={`note-delete-${n.id}`}><Trash2 className="h-3 w-3"/></Button>
            </div>
            <pre className="text-sm text-zinc-200 whitespace-pre-wrap font-sans">{n.body}</pre>
          </Card>
        ))}
      </div>
    </div>
  );
}
